/* Go Hollywood care finder: no requests, storage, tracking or cart mutations. */
(() => {
  'use strict';
  const TAG = 'gh-care-quiz';
  if (customElements.get(TAG)) return;

  const P = Object.freeze({
    shampoo: 'oatmeal-aloevera-shampoo', conditioner: 'aloe-oatmeal-conditioner',
    slicker: 'soft-pins-slicker-brush', comb: 'heavy-duty-fine-comb',
    wipes: 'hollywood-grooming-oatmeal-and-aloe-wipes', spray: 'sugar-cookies-body-spray',
    puppy: 'puppy-tearless-shampoo', whitening: 'a-soapfree-whitenning-shampo',
    deshedding: 'deshedding-brush', dental: 'dental-wipes', bow: 'pet-velvet-bowtie'
  });
  const QUESTIONS = Object.freeze({
    bath: { title: 'What does bath day call for?', choices: [
      ['wash', 'An everyday wash', 'Start with shampoo'], ['condition', 'A conditioning finish', 'Soften and help manage the coat'],
      ['puppy', 'Puppy bath time', 'Explore the puppy-care lineup'], ['brighten', 'A brighter-looking coat', 'A wash focused on coat appearance']
    ] },
    brush: { title: 'What would you like your tool to do?', choices: [
      ['short', 'Collect loose hair on a short coat', 'A tool for short-haired pets'], ['tangles', 'Work through everyday tangles', 'Brush, then finish with a comb'],
      ['comb', 'Finish with a comb', 'A closer pass through the coat']
    ] },
    between: { title: 'Which small care moment is on your list?', choices: [
      ['cleanup', 'A coat-and-paw cleanup', 'Wipes between full baths'], ['scent', 'A finishing scent', 'A sweet sugar-cookie fragrance'],
      ['dental', 'A dental cleanup', 'Wipes made for pet dental care']
    ] },
    finish: { title: 'Which finishing touch feels like them?', choices: [
      ['bow', 'A bow tie', 'Choose their color and fit on the product page'], ['scent', 'A finishing scent', 'A little sugar-cookie fragrance']
    ] }
  });
  const RULES = Object.freeze({
    'bath:wash': { primary: P.shampoo, pair: P.conditioner, reasons: ['Shampoo for the washing step of bath day.', 'Condition after washing for a soft, manageable finish.'] },
    'bath:condition': { primary: P.conditioner, pair: P.shampoo, reasons: ['Conditioning care to help soften and manage the coat.', 'Start the wash-day routine with shampoo.'] },
    'bath:puppy': { primary: P.puppy, reasons: ['A shampoo from the puppy-care lineup. Check the label for age and directions.'] },
    'bath:brighten': { primary: P.whitening, pair: P.conditioner, reasons: ['A shampoo focused on a brighter-looking coat.', 'Follow washing with a conditioning finish.'] },
    'brush:short': { primary: P.deshedding, reasons: ['A tool described for collecting loose undercoat on short-haired cats and dogs.'] },
    'brush:tangles': { primary: P.slicker, pair: P.comb, reasons: ['A soft-pin brush for loose hair and everyday tangles.', 'A comb for finishing work after brushing.'] },
    'brush:comb': { primary: P.comb, pair: P.slicker, reasons: ['A fine comb for working through and finishing the coat.', 'A soft-pin brush to start with loose hair and everyday tangles.'] },
    'between:cleanup': { primary: P.wipes, reasons: ['Keep wipes close for coat-and-paw cleanups between full baths.'] },
    'between:scent': { primary: P.spray, pair: P.wipes, reasons: ['A sugar-cookie scent for a finishing touch.', 'Wipes for quick coat-and-paw cleanups before the finishing step.'] },
    'between:dental': { primary: P.dental, category: 'dental', reasons: ['Wipes made for a pet dental-cleaning routine. Follow the product directions.'] },
    'finish:bow': { primary: P.bow, reasons: ['A bow tie that attaches to a collar. Choose the color and fit on its product page.'] },
    'finish:scent': { primary: P.spray, category: 'between', reasons: ['A sugar-cookie finishing scent for the grooming routine.'] }
  });

  // Pure rule function: missing/unavailable primary never promotes an unrelated pair.
  function recommend(state, available) {
    const rule = RULES[`${state.need}:${state.detail}`];
    if (!rule || !['one', 'pair'].includes(state.depth)) return { items: [], category: state.need || 'bath' };
    const result = { items: [], category: rule.category || state.need };
    if (!available.has(rule.primary)) return result;
    result.items.push({ key: rule.primary, reason: rule.reasons[0] });
    if (state.depth === 'pair' && rule.pair && rule.pair !== rule.primary && available.has(rule.pair)) {
      result.items.push({ key: rule.pair, reason: rule.reasons[1] });
    }
    return result;
  }

  class CareQuiz extends HTMLElement {
    static recommend(state, available) { return recommend(state, available); }
    static get choices() { return QUESTIONS; }
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.form = this.querySelector('[data-quiz-form]');
      this.fallback = this.querySelector('[data-quiz-fallback]');
      this.results = this.querySelector('[data-quiz-results]');
      this.fields = [...this.querySelectorAll('[data-quiz-step]')];
      this.status = this.querySelector('[data-quiz-status]');
      if (!this.form || !this.fallback || !this.results || this.fields.length !== 3 || !this.status) return;
      this.templates = new Map([...this.querySelectorAll('template[data-product-key]')].map(t => [t.dataset.productKey, t]));
      this.available = new Set([...this.templates].filter(([, t]) => t.dataset.available === 'true').map(([key]) => key));
      this.categories = new Map([...this.querySelectorAll('[data-category]')].map(a => [a.dataset.category, a.href]));
      const options = { signal: this.controller.signal };
      this.form.addEventListener('submit', event => { event.preventDefault(); this.next(); }, options);
      this.querySelector('[data-quiz-back]').addEventListener('click', () => this.showStep(this.state.step - 1), options);
      this.querySelector('[data-quiz-edit]').addEventListener('click', () => this.showStep(0), options);
      this.querySelector('[data-quiz-restart]').addEventListener('click', () => this.restart(true), options);
      this.restart(false);
      this.fallback.hidden = true;
    }
    disconnectedCallback() { this.controller?.abort(); }
    restart(focus) {
      this.form.reset();
      this.state = { step: 0, need: '', detail: '', depth: '' };
      this.querySelector('[data-detail-options]').replaceChildren();
      this.querySelector('[data-result-cards]').replaceChildren();
      this.status.textContent = '';
      this.showStep(0, focus);
    }
    showStep(index, focus = true) {
      this.status.textContent = '';
      this.state.step = Math.max(0, Math.min(2, index));
      this.results.hidden = true;
      this.form.hidden = false;
      this.fields.forEach((field, i) => { field.hidden = i !== this.state.step; field.disabled = i !== this.state.step; });
      this.querySelector('[data-quiz-back]').hidden = this.state.step === 0;
      this.querySelector('[data-quiz-next]').textContent = this.state.step === 2 ? 'See my essentials →' : 'Continue →';
      this.querySelector('[data-quiz-progress-text]').textContent = `Question ${this.state.step + 1} of 3`;
      this.querySelector('progress').value = this.state.step + 1;
      if (focus) this.fields[this.state.step].querySelector('legend').focus();
    }
    populateDetail() {
      const question = QUESTIONS[this.state.need];
      const holder = this.querySelector('[data-detail-options]');
      holder.replaceChildren();
      this.querySelector('[data-detail-question]').textContent = question.title;
      question.choices.forEach(([value, title, description]) => {
        const label = document.createElement('label');
        const input = document.createElement('input');
        input.type = 'radio'; input.name = this.fields[1].dataset.detailName; input.value = value; input.required = true;
        const span = document.createElement('span');
        const strong = document.createElement('strong'); strong.textContent = title;
        const small = document.createElement('small'); small.textContent = description;
        span.append(strong, small); label.append(input, span); holder.append(label);
      });
    }
    next() {
      if (!this.form.reportValidity()) return;
      const choice = this.fields[this.state.step].querySelector('input:checked')?.value;
      if (!choice) return;
      if (this.state.step === 0) {
        if (!Object.prototype.hasOwnProperty.call(QUESTIONS, choice)) return;
        if (this.state.need !== choice) {
          this.state.need = choice; this.state.detail = ''; this.state.depth = '';
          this.fields[2].querySelectorAll('input').forEach(input => { input.checked = false; });
          this.populateDetail();
        }
        this.showStep(1);
      } else if (this.state.step === 1) {
        if (!RULES[`${this.state.need}:${choice}`]) return;
        this.state.detail = choice; this.showStep(2);
      } else {
        this.state.depth = choice; this.showResults();
      }
    }
    showResults() {
      const result = recommend(this.state, this.available);
      const holder = this.querySelector('[data-result-cards]');
      holder.replaceChildren();
      result.items.forEach((item, index) => {
        const fragment = this.templates.get(item.key).content.cloneNode(true);
        fragment.querySelector('[data-product-reason]').textContent = item.reason;
        fragment.querySelector('[data-result-role]').textContent = index === 0 ? 'START HERE' : 'PAIR IT WITH';
        holder.append(fragment);
      });
      this.querySelector('[data-result-empty]').hidden = result.items.length > 0;
      this.querySelector('[data-result-category]').href = this.categories.get(result.category) || this.categories.get('bath');
      const heading = this.querySelector('[data-result-heading]');
      heading.textContent = result.items.length ? 'Your care edit.' : 'Let’s find another starting point.';
      this.querySelector('[data-result-description]').textContent = !result.items.length ? '' : result.items.length === 2
        ? 'Two complementary choices for the care moment you selected.'
        : this.state.depth === 'pair' ? 'One available essential fits this step. There’s no need to add more just to make a pair.'
          : 'One focused choice for the care moment you selected.';
      this.form.hidden = true;
      this.fields.forEach(field => { field.disabled = true; });
      this.results.hidden = false;
      heading.focus();
      this.status.textContent = result.items.length ? `${result.items.length} ${result.items.length === 1 ? 'product matches' : 'products match'} your choices.` : 'No available match. Browse the category or ask our team.';
    }
  }
  customElements.define(TAG, CareQuiz);
})();
